import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { ref, set, onValue, remove } from "firebase/database";

// ── Palette inspired by Indian festivals ──────────────────────────────
const C = {
  saffron: "#FF6B00",
  gold: "#FFB800",
  magenta: "#D4006A",
  teal: "#007A6E",
  purple: "#6A0572",
  cream: "#FFFAF4",
  cardBg: "#FFFFFF",
  text: "#1a1a2e",
  muted: "#6b6b8a",
  border: "#f0e8d8",
};

const styles = `
  @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
  @keyframes floatIn { from { opacity:0; transform:translateY(-20px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }

  .page { animation: fadeIn 0.5s ease both; }
  .card { background:#fff; border-radius:20px; box-shadow:0 4px 24px rgba(0,0,0,0.08); padding:28px; margin-bottom:20px; }
  .btn-primary {
    background: linear-gradient(135deg, ${C.saffron}, ${C.magenta});
    color:#fff; border:none; border-radius:50px; padding:14px 32px;
    font-family:'Baloo 2',sans-serif; font-size:16px; font-weight:700;
    cursor:pointer; transition:transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 16px rgba(255,107,0,0.35);
  }
  .btn-primary:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(255,107,0,0.45); }
  .btn-secondary {
    background:#fff; color:${C.saffron}; border:2px solid ${C.saffron};
    border-radius:50px; padding:10px 24px;
    font-family:'Baloo 2',sans-serif; font-size:14px; font-weight:700;
    cursor:pointer; transition:all 0.15s;
  }
  .btn-secondary:hover { background:${C.saffron}; color:#fff; }
  .btn-danger {
    background:#fff; color:#c0392b; border:2px solid #c0392b;
    border-radius:50px; padding:8px 18px;
    font-family:'Baloo 2',sans-serif; font-size:13px; font-weight:700;
    cursor:pointer; transition:all 0.15s;
  }
  .btn-danger:hover { background:#c0392b; color:#fff; }
  .input {
    width:100%; padding:12px 16px; border:2px solid ${C.border};
    border-radius:12px; font-family:'Nunito',sans-serif; font-size:15px;
    color:${C.text}; background:#fafafa; transition:border 0.2s;
    outline:none;
  }
  .input:focus { border-color:${C.saffron}; background:#fff; }
  .tag {
    display:inline-block; padding:4px 12px; border-radius:50px;
    font-size:12px; font-weight:700; margin:3px;
  }
  .modal-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,0.5);
    display:flex; align-items:center; justify-content:center;
    z-index:1000; padding:20px;
  }
  .modal { background:#fff; border-radius:24px; padding:32px; max-width:540px; width:100%; animation:floatIn 0.3s ease both; max-height:90vh; overflow-y:auto; }
  .spinner { width:32px; height:32px; border:3px solid ${C.border}; border-top-color:${C.saffron}; border-radius:50%; animation:spin 0.7s linear infinite; margin:0 auto; }
  .nav-link { cursor:pointer; padding:8px 16px; border-radius:50px; font-weight:700; font-size:14px; transition:all 0.15s; }
  .nav-link:hover { background:rgba(255,107,0,0.1); }
  .checkbox-label { display:flex; gap:10px; align-items:flex-start; cursor:pointer; font-size:13px; color:${C.muted}; line-height:1.5; }
  .checkbox-label input { margin-top:3px; accent-color:${C.saffron}; width:16px; height:16px; flex-shrink:0; }
`;

// ── Helpers ───────────────────────────────────────────────────────────
// Add email to mailing list (no duplicates — keyed by safe email)
async function addToMailingList(name, email) {
  if (!email) return;
  const safeKey = email.toLowerCase().replace(/[.#$[\]]/g, "_");
  await set(ref(db, `mailingList/${safeKey}`), {
    name,
    email: email.toLowerCase(),
    addedAt: new Date().toISOString(),
  });
}
function getNextSessions() {
  // Hardcoded upcoming sessions — add more dates here as you schedule them
  const hardcodedDates = [
    new Date(2026, 4, 31), // May 31, 2026
  ];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return hardcodedDates.filter(d => d >= today);
}

function formatDate(d) {
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function formatDateShort(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

const STYLES_OFFERED = ["Bollywood", "Bhangra", "Garba", "Raas", "Folk", "& more"];
const EMOJI_MAP = { Bollywood:"💃", Bhangra:"🥁", Garba:"🌀", Raas:"🪷", Folk:"🌾", "& more":"✨" };

// ── Main App ──────────────────────────────────────────────────────────
export default function App() {
  const [view, setView] = useState(() => {
    const hash = window.location.hash.replace("#", "");
    return ["register","unregister","diwali","admin"].includes(hash) ? hash : "home";
  });
  const [sessions, setSessions] = useState([]);
  const [registrations, setRegistrations] = useState({});
  const [adminMode, setAdminMode] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [adminError, setAdminError] = useState("");
  const ADMIN_PIN = "1234"; // ← CHANGE THIS to your preferred PIN

  useEffect(() => {
    // Load sessions from Firebase
    const sessRef = ref(db, "sessions");
    onValue(sessRef, snap => {
      const data = snap.val() || {};
      const list = Object.entries(data)
        .map(([id, s]) => ({ id, ...s }))
        .filter(s => new Date(s.date + "T12:00:00") >= new Date(new Date().setHours(0,0,0,0)))
        .sort((a, b) => a.date.localeCompare(b.date));
      setSessions(list);
    });
    const regRef = ref(db, "registrations");
    onValue(regRef, snap => {
      setRegistrations(snap.val() || {});
    });
  }, []);

  const navigate = (v) => {
    setView(v);
    window.location.hash = v === "home" ? "" : v;
  };

  const enterAdmin = () => {
    if (adminPin === ADMIN_PIN) { setAdminMode(true); setAdminError(""); }
    else setAdminError("Incorrect PIN");
  };

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight:"100vh", background: C.cream, fontFamily:"'Nunito',sans-serif" }}>
        <NavBar view={view} setView={navigate} adminMode={adminMode} setAdminMode={setAdminMode} />

        {view === "home" && <HomePage sessions={sessions} registrations={registrations} setView={navigate} />}
        {view === "register" && <RegisterPage sessions={sessions} registrations={registrations} setView={navigate} />}
        {view === "unregister" && <UnregisterPage registrations={registrations} />}
        {view === "diwali" && <DiwaliForm setView={navigate} />}
        {view === "admin" && !adminMode && (
          <AdminLogin adminPin={adminPin} setAdminPin={setAdminPin} enterAdmin={enterAdmin} adminError={adminError} />
        )}
        {view === "admin" && adminMode && (
          <AdminPage sessions={sessions} registrations={registrations} />
        )}
      </div>
    </>
  );
}

// ── Mandala Pattern Banner ────────────────────────────────────────────
function DanceBanner() {
  const Mandala = ({ cx, cy, outerColor, innerColor, coreColor }) => (
    <g transform={`translate(${cx},${cy})`}>
      <circle r="52" fill="none" stroke={outerColor} strokeWidth="1" opacity="0.4"/>
      <circle r="44" fill="none" stroke="#FFD700" strokeWidth="1.2" opacity="0.5"/>
      {[0,30,60,90,120,150,180,210,240,270,300,330].map(a => (
        <ellipse key={a} cx="0" cy="-44" rx="5" ry="10" fill={outerColor} transform={`rotate(${a})`} opacity="0.75"/>
      ))}
      {[0,45,90,135,180,225,270,315].map(a => (
        <ellipse key={a} cx="0" cy="-28" rx="4" ry="8" fill="#FFD700" transform={`rotate(${a})`} opacity="0.9"/>
      ))}
      {[0,60,120,180,240,300].map(a => (
        <ellipse key={a} cx="0" cy="-16" rx="3" ry="6" fill={innerColor} transform={`rotate(${a})`} opacity="0.95"/>
      ))}
      <circle r="8" fill={coreColor}/>
      <circle r="4" fill="#FFD700"/>
      <circle r="2" fill="#fff" opacity="0.8"/>
    </g>
  );

  return (
    <svg viewBox="0 0 900 240" xmlns="http://www.w3.org/2000/svg" style={{ display:"block", width:"100%", height:"auto" }}>
      {/* Background */}
      <rect width="900" height="240" fill="#1a0033"/>
      {/* Diamond grid */}
      <defs>
        <pattern id="dg" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
          <polygon points="30,4 56,30 30,56 4,30" fill="none" stroke="#FFD700" strokeWidth="0.8" opacity="0.35"/>
          <polygon points="30,14 46,30 30,46 14,30" fill="none" stroke="#FF6B00" strokeWidth="0.6" opacity="0.3"/>
          <circle cx="30" cy="30" r="2" fill="#FFD700" opacity="0.25"/>
        </pattern>
      </defs>
      <rect width="900" height="240" fill="url(#dg)"/>

      {/* Top border */}
      <rect x="0" y="0" width="900" height="18" fill="#B8860B" opacity="0.9"/>
      <rect x="0" y="3" width="900" height="2" fill="#FFD700"/>
      <rect x="0" y="13" width="900" height="2" fill="#FFD700"/>
      <polyline points="0,8 15,3 30,8 45,3 60,8 75,3 90,8 105,3 120,8 135,3 150,8 165,3 180,8 195,3 210,8 225,3 240,8 255,3 270,8 285,3 300,8 315,3 330,8 345,3 360,8 375,3 390,8 405,3 420,8 435,3 450,8 465,3 480,8 495,3 510,8 525,3 540,8 555,3 570,8 585,3 600,8 615,3 630,8 645,3 660,8 675,3 690,8 705,3 720,8 735,3 750,8 765,3 780,8 795,3 810,8 825,3 840,8 855,3 870,8 885,3 900,8" fill="none" stroke="#FFD700" strokeWidth="1.5"/>

      {/* Bottom border */}
      <rect x="0" y="222" width="900" height="18" fill="#B8860B" opacity="0.9"/>
      <rect x="0" y="224" width="900" height="2" fill="#FFD700"/>
      <rect x="0" y="234" width="900" height="2" fill="#FFD700"/>
      <polyline points="0,232 15,237 30,232 45,237 60,232 75,237 90,232 105,237 120,232 135,237 150,232 165,237 180,232 195,237 210,232 225,237 240,232 255,237 270,232 285,237 300,232 315,237 330,232 345,237 360,232 375,237 390,232 405,237 420,232 435,237 450,232 465,237 480,232 495,237 510,232 525,237 540,232 555,237 570,232 585,237 600,232 615,237 630,232 645,237 660,232 675,237 690,232 705,237 720,232 735,237 750,232 765,237 780,232 795,237 810,232 825,237 840,232 855,237 870,232 885,237 900,232" fill="none" stroke="#FFD700" strokeWidth="1.5"/>

      {/* 5 evenly spaced mandalas — no edge overlap */}
      <Mandala cx={90}  cy={120} outerColor="#FF3D7F" innerColor="#FF6B00" coreColor="#C62828"/>
      <Mandala cx={270} cy={120} outerColor="#00BCD4" innerColor="#E91E8C" coreColor="#00695C"/>
      <Mandala cx={450} cy={120} outerColor="#FFD700" innerColor="#FF6B00" coreColor="#B8860B"/>
      <Mandala cx={630} cy={120} outerColor="#9C27B0" innerColor="#FFD700" coreColor="#6A0572"/>
      <Mandala cx={810} cy={120} outerColor="#4CAF50" innerColor="#FF3D7F" coreColor="#1B5E20"/>

      {/* Between-mandala diamonds */}
      {[170, 350, 530, 710].map(x => (
        <g key={x}>
          <polygon points={`${x},120 ${x+10},110 ${x+20},120 ${x+10},130`} fill="#FFD700" opacity="0.6"/>
          <polygon points={`${x},120 ${x+10},110 ${x+20},120 ${x+10},130`} fill="none" stroke="#FF6B00" strokeWidth="1"/>
        </g>
      ))}

      {/* Title text inside the banner */}
      <text x="450" y="108" textAnchor="middle" fontFamily="'Baloo 2', sans-serif" fontWeight="800" fontSize="36" fill="#FFD700" opacity="0.0">Indian Dance Class for Kids</text>
    </svg>
  );
}

// ── NavBar ─────────────────────────────────────────────────────────────
function NavBar({ view, setView, adminMode, setAdminMode }) {
  return (
    <nav style={{ background:"#fff", borderBottom:`3px solid ${C.gold}`, padding:"0 16px", display:"flex", alignItems:"center", justifyContent:"space-between", height:64, position:"sticky", top:0, zIndex:100, boxShadow:"0 2px 12px rgba(0,0,0,0.07)" }}>
      <div onClick={() => setView("home")} style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:28 }}>🪷</span>
        <div>
          <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:15, color:C.saffron, lineHeight:1 }}>Indian Dance</div>
          <div style={{ fontFamily:"'Baloo 2'", fontWeight:600, fontSize:11, color:C.muted, lineHeight:1 }}>Kids · Jupiter, FL</div>
        </div>
      </div>
      <div style={{ display:"flex", gap:2, alignItems:"center", flexWrap:"wrap" }}>
        <span className="nav-link" style={{ color: view==="home" ? C.saffron : C.text, fontSize:13 }} onClick={() => setView("home")}>Home</span>
        <span className="nav-link" style={{ color: view==="register" ? C.saffron : C.text, fontSize:13 }} onClick={() => setView("register")}>Register</span>
        <span className="nav-link" style={{ color: view==="unregister" ? C.saffron : C.text, fontSize:13 }} onClick={() => setView("unregister")}>Un-Register</span>
        <span className="nav-link" style={{ color: view==="admin" ? C.saffron : C.muted, fontSize:11 }} onClick={() => { setView("admin"); setAdminMode(false); }}>Admin</span>
      </div>
    </nav>
  );
}

// ── Hero / Home ────────────────────────────────────────────────────────
function HomePage({ sessions, registrations, setView }) {
  return (
    <div className="page">
      {/* Hero — banner IS the hero, title overlaid on top */}
      <div style={{ background:`linear-gradient(135deg, #FF6B00 0%, #D4006A 50%, #6A0572 100%)`, textAlign:"center", position:"relative", overflow:"hidden" }}>
        {/* Decorative circles */}
        <div style={{ position:"absolute", top:-40, left:-40, width:180, height:180, borderRadius:"50%", background:"rgba(255,255,255,0.05)" }}/>
        <div style={{ position:"absolute", bottom:-60, right:-20, width:240, height:240, borderRadius:"50%", background:"rgba(255,255,255,0.04)" }}/>

        {/* Banner with title overlaid */}
        <div style={{ position:"relative", width:"100%", maxWidth:900, margin:"0 auto" }}>
          <DanceBanner />
          {/* Title overlaid on center of banner */}
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
            <h1 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:"clamp(22px,4.5vw,44px)", color:"#FFD700", lineHeight:1.1, margin:0, textShadow:"0 2px 12px rgba(0,0,0,0.7), 0 0 40px rgba(0,0,0,0.5)" }}>
              Indian Dance Class for Kids
            </h1>
          </div>
        </div>

        {/* Subtitle and CTA below banner */}
        <div style={{ padding:"28px 24px 48px" }}>
          <p style={{ color:"rgba(255,255,255,0.95)", fontSize:"clamp(15px,2.5vw,20px)", fontWeight:600, marginBottom:6 }}>
            Join us to learn Bollywood, Bhangra, Garba, Raas, Folk, and more!
          </p>
          <p style={{ color:"rgba(255,255,255,0.75)", fontSize:15, marginBottom:24 }}>
            Located in Jupiter / Palm Beach area · Free monthly class
          </p>
          <button className="btn-primary" style={{ background:"#FFD700", color:"#7a3d00", boxShadow:"0 4px 20px rgba(0,0,0,0.25)", fontWeight:800 }} onClick={() => setView("register")}>
            Sign Up for a Class →
          </button>
        </div>
      </div>

      <div style={{ maxWidth:780, margin:"0 auto", padding:"32px 20px" }}>

        {/* Quick links bar */}
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:28, padding:"16px 20px", background:"#fff", borderRadius:16, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1px solid ${C.border}` }}>
          <span style={{ color:C.muted, fontSize:13, fontWeight:700, alignSelf:"center", marginRight:4 }}>Jump to:</span>
          <a href="#mailing" style={{ textDecoration:"none" }}><span className="tag" style={{ background:`${C.magenta}15`, color:C.magenta, fontWeight:700, cursor:"pointer", fontSize:13, padding:"6px 14px" }}>📬 Mailing List</span></a>
          <a href="#classes" style={{ textDecoration:"none" }}><span className="tag" style={{ background:`${C.saffron}15`, color:C.saffron, fontWeight:700, cursor:"pointer", fontSize:13, padding:"6px 14px" }}>📅 Sign Up for a Class</span></a>
        </div>

        {/* Mailing List */}
        <div id="mailing"><MailingListSignup /></div>

        {/* Next Sessions */}
        <div id="classes">
        <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24, color:C.text, marginBottom:16 }}>📅 Upcoming Classes</h2>
        <div style={{ display:"grid", gap:12, marginBottom:32 }}>
          {sessions.length === 0 && (
            <div className="card" style={{ textAlign:"center", color:C.muted }}>
              <p style={{ fontSize:32, marginBottom:8 }}>📅</p>
              <p>No upcoming classes scheduled yet. Check back soon!</p>
            </div>
          )}
          {sessions.map((s, i) => (
            <div key={s.id} className="card" style={{ padding:"18px 24px", border: i===0 ? `2px solid ${C.saffron}` : `1px solid ${C.border}` }}>
              {i===0 && <span className="tag" style={{ background:C.saffron, color:"#fff", marginBottom:6, display:"inline-block" }}>Next Class</span>}
              <div style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text }}>{formatDate(new Date(s.date + "T12:00:00"))}</div>
              <div style={{ color:C.muted, fontSize:14, marginTop:4 }}>🕙 {s.time || "10:00 AM – 11:00 AM"}</div>
              <div style={{ color:C.text, fontSize:14, fontWeight:600, marginTop:4 }}>📍 {s.location || "TBD"}</div>
              {s.address && <div style={{ color:C.muted, fontSize:13, marginTop:2, marginLeft:20 }}>{s.address}</div>}
              {s.address && (
                <a href={`https://maps.google.com/?q=${encodeURIComponent((s.location || "") + " " + s.address)}`} target="_blank" rel="noreferrer"
                  style={{ display:"inline-block", color:C.teal, fontWeight:700, fontSize:13, textDecoration:"none", marginTop:8, marginLeft:20 }}>
                  → Open in Google Maps
                </a>
              )}
              {s.description && (
                <div style={{ marginTop:12, background:`${C.gold}20`, borderRadius:10, padding:"10px 14px", borderLeft:`3px solid ${C.gold}` }}>
                  <p style={{ color:"#7a5200", fontSize:14, fontWeight:600, margin:0 }}>✨ {s.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>{/* end #classes */}

        {/* About */}
        <div className="card" style={{ border:`2px solid ${C.gold}` }}>
          <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:22, color:C.text, marginBottom:12 }}>🎶 About the Class</h2>
          <p style={{ color:C.text, lineHeight:1.7, marginBottom:12 }}>
            A free monthly dance class for kids in the Jupiter and Palm Beach area, celebrating the joy and richness of South Asian dance forms — from the energetic beats of <strong>Bhangra</strong> to the graceful storytelling of <strong>Bollywood</strong>, the spinning circles of <strong>Garba</strong>, and the vibrant footwork of <strong>Raas</strong>.
          </p>
          <p style={{ color:C.text, lineHeight:1.7, marginBottom:12 }}>
            Classes are held once a month on Sunday mornings and taught by volunteers passionate about sharing South Asian culture through movement. All skill levels welcome!
          </p>
          <div style={{ background:"#f9f6f0", borderRadius:12, padding:14, fontSize:14, color:C.text, lineHeight:1.7 }}>
            <p><strong>📌 Important reminders:</strong></p>
            <ul style={{ paddingLeft:18, marginTop:6 }}>
              <li>Parents and / or caregivers must be present through the full duration of the class.</li>
              <li>No child can be left unattended.</li>
              <li>Please bring water for your child.</li>
              <li>No food will be allowed.</li>
            </ul>
          </div>
        </div>

        {/* Cost & Donations */}
        <div className="card" style={{ background:`linear-gradient(135deg, #fff8f0, #fff)`, border:`2px solid ${C.gold}60` }}>
          <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:22, color:C.text, marginBottom:10 }}>💛 Free Class + Donations</h2>
          <p style={{ color:C.text, lineHeight:1.7 }}>
            There is no cost to this class, though donations are greatly appreciated to help cover the cost of room rental.
          </p>
          <div style={{ background:`${C.gold}22`, borderRadius:12, padding:"10px 14px", margin:"12px 0", display:"inline-block" }}>
            <span style={{ fontWeight:800, color:"#7a5200", fontSize:15 }}>💵 Suggested donation: $5–$10 per child</span>
          </div>
          <div style={{ marginTop:8, display:"flex", gap:12, flexWrap:"wrap" }}>
            <span style={{ background:C.teal, color:"#fff", borderRadius:12, padding:"8px 16px", fontSize:13, fontWeight:700 }}>💳 Zelle: 904-294-2010</span>
            <span style={{ background:C.purple, color:"#fff", borderRadius:12, padding:"8px 16px", fontSize:13, fontWeight:700 }}>📱 Venmo: 904-294-2010</span>
          </div>
        </div>

        {/* Contact */}
        <div className="card">
          <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:22, color:C.text, marginBottom:12 }}>✉️ Contact</h2>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <a href="mailto:sejal.shah.n@gmail.com" style={{ color:C.saffron, fontWeight:700, textDecoration:"none", fontSize:15 }}>📧 sejal.shah.n@gmail.com</a>
            <a href="tel:9042942010" style={{ color:C.saffron, fontWeight:700, textDecoration:"none", fontSize:15 }}>📞 904-294-2010</a>
          </div>
        </div>

        <div style={{ textAlign:"center", padding:"24px 0 8px" }}>
          <button className="btn-primary" onClick={() => setView("register")}>Register for a Class 🪷</button>
        </div>
      </div>
    </div>
  );
}

// ── Mailing List Signup ────────────────────────────────────────────────
function MailingListSignup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(""); // "" | "success" | "error"
  const [loading, setLoading] = useState(false);

  const handleSignup = async () => {
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    try {
      await addToMailingList(name.trim(), email.trim());
      setStatus("success");
      setName(""); setEmail("");
    } catch (e) {
      setStatus("error");
    }
    setLoading(false);
  };

  return (
    <div className="card" style={{ border:`2px solid ${C.magenta}40`, background:"linear-gradient(135deg, #fff0f6, #fff)" }}>
      <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:22, color:C.text, marginBottom:6 }}>📬 Stay in the Loop</h2>
      <p style={{ color:C.muted, fontSize:14, lineHeight:1.6, marginBottom:16 }}>
        Sign up to be notified when new class dates are announced!
      </p>
      {status === "success" ? (
        <div style={{ background:`${C.teal}15`, border:`1px solid ${C.teal}`, borderRadius:12, padding:16, color:C.teal, fontWeight:700, textAlign:"center" }}>
          ✓ You're on the list! We'll notify you when new classes are scheduled.
        </div>
      ) : (
        <div style={{ display:"grid", gap:10 }}>
          <input className="input" placeholder="Your name" value={name} onChange={e => setName(e.target.value)}/>
          <input className="input" type="email" placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSignup()}/>
          {status === "error" && <p style={{ color:"#c0392b", fontSize:13 }}>Something went wrong. Please try again.</p>}
          <button className="btn-primary" onClick={handleSignup} disabled={loading || !name || !email}
            style={{ alignSelf:"flex-start", opacity: (!name || !email) ? 0.6 : 1 }}>
            {loading ? "Signing up..." : "Notify Me →"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Register Page ──────────────────────────────────────────────────────
function RegisterPage({ sessions, registrations, setView }) {
  const [selectedSession, setSelectedSession] = useState(null);
  const [numKids, setNumKids] = useState(1);
  const [kids, setKids] = useState([{ name:"", age:"" }]);
  const [form, setForm] = useState({ parentName:"", email:"", phone:"" });
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");

  const handleNumKidsChange = (n) => {
    const num = Math.max(1, Math.min(10, Number(n)));
    setNumKids(num);
    setKids(prev => {
      const updated = [...prev];
      while (updated.length < num) updated.push({ name:"", age:"" });
      return updated.slice(0, num);
    });
  };

  const updateKid = (i, field, value) => {
    setKids(prev => prev.map((k, idx) => idx === i ? { ...k, [field]: value } : k));
  };

  const handleSubmit = async () => {
    if (!selectedSession) return setError("Please select a class date.");
    if (!form.parentName || !form.email || !form.phone) return setError("Please fill in all parent/caregiver fields.");
    for (let i = 0; i < kids.length; i++) {
      if (!kids[i].name || !kids[i].age) return setError(`Please fill in name and age for child ${i + 1}.`);
    }
    if (!agreed) return setError("Please acknowledge the disclaimer to continue.");
    setError("");
    setLoading(true);
    try {
      const key = selectedSession.date;
      const safeEmail = form.email.split("@")[0].replace(/[.#$[\]]/g, "_");
      const code = `${safeEmail}-${key}`;
      const regPath = ref(db, `registrations/${key}/${code}`);
      await set(regPath, {
        kids,
        parentName: form.parentName,
        email: form.email,
        phone: form.phone,
        registeredAt: new Date().toISOString(),
        sessionDate: formatDate(new Date(selectedSession.date + "T12:00:00")),
        location: selectedSession.location || "Ignite Sports Center",
      });
      setRegistrationCode(code);
      // Auto-add to mailing list (no duplicates)
      await addToMailingList(form.parentName, form.email);
      setStep("success");
    } catch (e) {
      console.error("Firebase error:", e);
      setError(`Error: ${e.message || "Something went wrong. Please try again."}`);
    }
    setLoading(false);
  };

  if (step === "success") return (
    <div className="page" style={{ maxWidth:560, margin:"40px auto", padding:"0 20px" }}>
      <div className="card" style={{ textAlign:"center", border:`2px solid ${C.teal}` }}>
        <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
        <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:26, color:C.teal, marginBottom:8 }}>You're Registered!</h2>
        <p style={{ color:C.text, marginBottom:4 }}><strong>{kids.map(k=>k.name).join(", ")}</strong> {kids.length > 1 ? "are" : "is"} signed up for:</p>
        <p style={{ color:C.saffron, fontWeight:700, fontSize:16, marginBottom:20 }}>{formatDate(new Date(selectedSession.date + "T12:00:00"))} · {selectedSession.time || "10–11 AM"}</p>
        <div style={{ background:`${C.teal}12`, borderRadius:12, padding:16, marginBottom:16 }}>
          <p style={{ color:C.muted, fontSize:14, lineHeight:1.6 }}>
            If your plans change, you can un-register anytime using the <strong>"Un-Register"</strong> link in the menu — just enter the email address you used to sign up.
          </p>
        </div>
        <div style={{ background:"#fff3cd", border:"2px solid #ffc107", borderRadius:12, padding:14, marginBottom:20, textAlign:"left" }}>
          <p style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:14, color:"#7a5200", marginBottom:4 }}>⚠️ Please un-register if your plans change!</p>
          <p style={{ fontSize:13, color:"#7a5200", lineHeight:1.6, margin:0 }}>
            If you can no longer attend, please un-register <strong>at least 24 hours in advance</strong> so we have an accurate headcount. Thank you!
          </p>
        </div>
        <button className="btn-primary" onClick={() => setView("home")}>Back to Home</button>
      </div>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth:600, margin:"0 auto", padding:"32px 20px" }}>
      <h1 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:28, color:C.text, marginBottom:6 }}>Register for a Class</h1>
      <p style={{ color:C.muted, marginBottom:24 }}>Select a date and fill in your details below.</p>

      {/* Session Picker */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:12 }}>1. Choose a Class Date</h3>
        <div style={{ display:"grid", gap:10 }}>
          {sessions.length === 0 && <p style={{ color:C.muted }}>No upcoming sessions scheduled yet. Check back soon!</p>}
          {sessions.map((s, i) => {
            const selected = selectedSession && selectedSession.id === s.id;
            return (
              <div key={s.id} onClick={() => setSelectedSession(s)}
                style={{ border:`2px solid ${selected ? C.saffron : C.border}`, borderRadius:14, padding:"14px 18px", cursor:"pointer", background: selected ? `${C.saffron}12` : "#fafafa", transition:"all 0.15s" }}>
                <div style={{ fontWeight:700, color:C.text, fontSize:15 }}>{formatDate(new Date(s.date + "T12:00:00"))}</div>
                <div style={{ color:C.muted, fontSize:13, marginTop:2 }}>{s.time || "10:00 – 11:00 AM"} · {s.location || "Ignite Sports Center"}</div>
                {s.address && <div style={{ color:C.muted, fontSize:12 }}>{s.address}</div>}
                {s.description && <div style={{ color:C.saffron, fontSize:13, fontWeight:600, marginTop:6 }}>✨ {s.description}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Number of kids */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:12 }}>2. How many children are you registering?</h3>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={() => handleNumKidsChange(numKids - 1)}
            style={{ width:36, height:36, borderRadius:"50%", border:`2px solid ${C.saffron}`, background:"#fff", color:C.saffron, fontSize:20, fontWeight:700, cursor:"pointer", lineHeight:1 }}>−</button>
          <span style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:26, color:C.saffron, minWidth:32, textAlign:"center" }}>{numKids}</span>
          <button onClick={() => handleNumKidsChange(numKids + 1)}
            style={{ width:36, height:36, borderRadius:"50%", border:`2px solid ${C.saffron}`, background:"#fff", color:C.saffron, fontSize:20, fontWeight:700, cursor:"pointer", lineHeight:1 }}>+</button>
          <span style={{ color:C.muted, fontSize:14 }}>child{numKids !== 1 ? "ren" : ""}</span>
        </div>
      </div>

      {/* Kids info */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:16 }}>3. Children's Information</h3>
        <div style={{ display:"grid", gap:18 }}>
          {kids.map((kid, i) => (
            <div key={i} style={{ background:"#f9f6f0", borderRadius:12, padding:14 }}>
              <p style={{ fontWeight:700, color:C.saffron, fontSize:14, marginBottom:10 }}>Child {i + 1}</p>
              <div style={{ display:"grid", gap:10 }}>
                <div>
                  <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Name *</label>
                  <input className="input" placeholder="e.g. Aarav Patel" value={kid.name} onChange={e => updateKid(i, "name", e.target.value)} />
                </div>
                <div>
                  <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Age *</label>
                  <input className="input" type="number" min="1" max="18" placeholder="e.g. 7" value={kid.age} onChange={e => updateKid(i, "age", e.target.value)} style={{ maxWidth:120 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Parent info */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:16 }}>4. Parent / Caregiver Information</h3>
        <div style={{ display:"grid", gap:14 }}>
          <div>
            <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Parent / Caregiver Name *</label>
            <input className="input" placeholder="e.g. Priya Patel" value={form.parentName} onChange={e => setForm(f=>({...f, parentName:e.target.value}))} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Email Address *</label>
            <input className="input" type="email" placeholder="e.g. priya@email.com" value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Phone Number *</label>
            <input className="input" type="tel" placeholder="e.g. (561) 555-1234" value={form.phone} onChange={e => setForm(f=>({...f, phone:e.target.value}))} />
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="card" style={{ border:`1px solid ${C.gold}60` }}>
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:10 }}>5. Disclaimer & Acknowledgement</h3>
        <div style={{ background:"#f9f6f0", borderRadius:10, padding:14, marginBottom:14, fontSize:13, color:C.muted, lineHeight:1.65 }}>
          Parents and / or caregivers are responsible for being present during the full duration of the class. No child may be left unattended by direct parent and / or caregiver. Parents and / or caregivers retain full responsibility for the care, safety, and wellbeing of their children. For children that may be unable to participate or uncooperative, parents will be responsible for working with their child or removing them from the class.
          <br/><br/>
          This class is being done on a volunteer basis. Teachers and assistants will not be held responsible for anyone in attendance of the class.
        </div>
        <label className="checkbox-label">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
          I have read and agree to the disclaimer above.
        </label>
      </div>

      {error && <p style={{ color:"#c0392b", fontWeight:700, marginBottom:12, textAlign:"center" }}>{error}</p>}

      {/* Donation reminder */}
      <div style={{ background:"linear-gradient(135deg, #fff8f0, #fffdf5)", border:`2px solid ${C.gold}`, borderRadius:16, padding:"16px 20px", marginBottom:12 }}>
        <p style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:15, color:"#7a5200", marginBottom:4 }}>💛 Donations appreciated!</p>
        <p style={{ fontSize:14, color:"#7a5200", lineHeight:1.6 }}>
          This class is free, but we suggest a <strong>$5–$10 donation per child</strong> to help cover room rental costs. You can donate via <strong>Zelle or Venmo to 904-294-2010</strong>. Thank you for your support!
        </p>
      </div>

      {/* Un-register reminder */}
      <div style={{ background:"#fff3cd", border:"2px solid #ffc107", borderRadius:16, padding:"16px 20px", marginBottom:20 }}>
        <p style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:15, color:"#7a5200", marginBottom:4 }}>⚠️ Please un-register if your plans change!</p>
        <p style={{ fontSize:14, color:"#7a5200", lineHeight:1.6 }}>
          If you can no longer attend, <strong>please un-register at least 24 hours in advance</strong> so we have an accurate headcount. Use the "Un-Register" link in the menu at any time. This helps us plan better for everyone — thank you!
        </p>
      </div>

      <div style={{ textAlign:"center", paddingBottom:40 }}>
        {loading ? <div className="spinner" /> : (
          <button className="btn-primary" onClick={handleSubmit} style={{ fontSize:17 }}>Complete Registration 🪷</button>
        )}
      </div>
    </div>
  );
}

// ── Un-Register Page ───────────────────────────────────────────────────
function UnregisterPage({ registrations }) {
  const [email, setEmail] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [done, setDone] = useState(null);

  const search = () => {
    if (!email.trim()) return;
    setLoading(true);
    setResults(null);
    setTimeout(() => {
      const found = [];
      Object.entries(registrations).forEach(([dateKey, dateRegs]) => {
        Object.entries(dateRegs).forEach(([code, reg]) => {
          if (reg.email && reg.email.toLowerCase() === email.trim().toLowerCase()) {
            found.push({ dateKey, code, ...reg });
          }
        });
      });
      setResults(found);
      setLoading(false);
    }, 400);
  };

  const unregister = async (dateKey, code) => {
    setRemoving(code);
    try {
      await remove(ref(db, `registrations/${dateKey}/${code}`));
      setDone(code);
      setResults(r => r.filter(x => x.code !== code));
    } catch (e) { alert("Error removing. Please try again."); }
    setRemoving(null);
  };

  return (
    <div className="page" style={{ maxWidth:580, margin:"0 auto", padding:"32px 20px" }}>
      <h1 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:28, color:C.text, marginBottom:6 }}>Un-Register</h1>
      <p style={{ color:C.muted, marginBottom:24 }}>Enter the email you used to register to find and cancel your registration.</p>

      <div className="card">
        <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:8 }}>Your Email Address</label>
        <div style={{ display:"flex", gap:10 }}>
          <input className="input" type="email" placeholder="e.g. priya@email.com" value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key==="Enter" && search()} style={{ flex:1 }} />
          <button className="btn-secondary" onClick={search}>Search</button>
        </div>
      </div>

      {loading && <div style={{ textAlign:"center", padding:32 }}><div className="spinner" /></div>}

      {results && results.length === 0 && (
        <div className="card" style={{ textAlign:"center", color:C.muted }}>
          <p style={{ fontSize:32, marginBottom:8 }}>🔍</p>
          <p>No registrations found for <strong>{email}</strong>.</p>
        </div>
      )}

      {results && results.length > 0 && (
        <div>
          <p style={{ color:C.muted, fontSize:14, marginBottom:12 }}>Found {results.length} registration(s):</p>
          {results.map(reg => (
            <div key={reg.code} className="card" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div>
                <div style={{ fontWeight:700, color:C.text, fontSize:15 }}>{reg.childName}</div>
                <div style={{ color:C.muted, fontSize:13 }}>{reg.sessionDate}</div>
                <div style={{ color:C.muted, fontSize:12 }}>Parent: {reg.parentName}</div>
              </div>
              <button className="btn-danger" onClick={() => unregister(reg.dateKey, reg.code)} disabled={removing===reg.code}>
                {removing===reg.code ? "..." : "Remove"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Diwali Performance Interest Form ──────────────────────────────────
function DiwaliForm({ setView }) {
  const emptyKid = { name:"", age:"" };
  const [numKids, setNumKids] = useState(1);
  const [kids, setKids] = useState([{ ...emptyKid }]);
  const [parent, setParent] = useState({ name:"", email:"", phone:"" });
  const [checks, setChecks] = useState({ showInfo:false, practices:false, fees:false, expectations:false });
  const [initials, setInitials] = useState({ showInfo:"", practices:"", fees:"", expectations:"" });
  const [step, setStep] = useState("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleNumKids = (n) => {
    const num = Math.max(1, Math.min(3, Number(n)));
    setNumKids(num);
    setKids(prev => {
      const u = [...prev];
      while (u.length < num) u.push({ ...emptyKid });
      return u.slice(0, num);
    });
  };

  const updateKid = (i, field, val) => setKids(prev => prev.map((k, idx) => idx===i ? {...k, [field]:val} : k));
  const toggleCheck = (key) => setChecks(c => ({...c, [key]:!c[key]}));

  const handleSubmit = async () => {
    if (!parent.name || !parent.email || !parent.phone) return setError("Please fill in your name, email and phone number.");
    for (let i=0; i<kids.length; i++) {
      if (!kids[i].name || !kids[i].age) return setError(`Please fill in name and age for child ${i+1}.`);
      if (!kids[i].gender) return setError(`Please select a gender for child ${i+1}.`);
    }
    const allChecked = Object.values(checks).every(v => v);
    const allInitialed = Object.values(initials).every(v => v.trim().length > 0);
    if (!allChecked || !allInitialed) return setError("Please check and initial all sections to acknowledge you have read them.");
    setError("");
    setLoading(true);
    try {
      const safeKey = parent.email.toLowerCase().replace(/[.#$[\]]/g, "_") + "-" + Date.now();
      await set(ref(db, `diwaliInterest/${safeKey}`), {
        kids, parent, checks, initials,
        submittedAt: new Date().toISOString(),
      });
      setStep("success");
    } catch(e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const SectionBox = ({ id, title, children }) => (
    <div style={{ background:"#faf7ff", borderRadius:14, padding:18, border:`1px solid ${C.purple}30`, marginBottom:14 }}>
      <p style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:15, color:C.purple, marginBottom:10 }}>{title}</p>
      <div style={{ color:C.text, fontSize:13, lineHeight:1.75, marginBottom:14 }}>{children}</div>
      <div style={{ display:"flex", gap:16, alignItems:"center", flexWrap:"wrap", background:"#fff", borderRadius:10, padding:"12px 14px", border:`1px solid ${C.purple}20` }}>
        <label className="checkbox-label" style={{ fontWeight:700, color:C.purple }}>
          <input type="checkbox" checked={checks[id]} onChange={() => toggleCheck(id)} style={{ accentColor:C.purple }}/>
          I have read and understand this section
        </label>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto" }}>
          <label style={{ fontSize:12, fontWeight:700, color:C.muted }}>Initials:</label>
          <input className="input" value={initials[id]} onChange={e => setInitials(x => ({...x, [id]:e.target.value}))}
            placeholder="e.g. SS" style={{ width:70, padding:"6px 10px", textAlign:"center", fontWeight:700 }}/>
        </div>
      </div>
    </div>
  );

  if (step === "success") return (
    <div className="page" style={{ maxWidth:540, margin:"40px auto", padding:"0 20px" }}>
      <div className="card" style={{ textAlign:"center", border:`2px solid ${C.purple}` }}>
        <div style={{ fontSize:56, marginBottom:12 }}>🪔</div>
        <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24, color:C.purple, marginBottom:8 }}>Interest Submitted!</h2>
        <p style={{ color:C.text, lineHeight:1.7, marginBottom:20 }}>
          Thank you! Your interest in the Diwali performance has been received. We'll be in touch with next steps.
        </p>
        <button className="btn-primary" onClick={() => setView("home")}>Back to Home</button>
      </div>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth:640, margin:"0 auto", padding:"32px 20px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
        <span style={{ fontSize:36 }}>🪔</span>
        <h1 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:26, color:C.text, margin:0 }}>Diwali Performance — Interest Form</h1>
      </div>
      <p style={{ color:C.muted, marginBottom:8, lineHeight:1.6 }}>
        Before we know it, festival season will be upon us! Please read all the details carefully and fill out this form by <strong>Friday, August 7th</strong> if you're interested in having your child participate in a Diwali dance for the PBIA show.
      </p>
      <p style={{ color:C.muted, fontSize:13, marginBottom:24, fontStyle:"italic" }}>
        Note: Filling out this form is not a guarantee of a spot — we'll aim to include as many kids as possible.
      </p>

      {/* Kids */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:12 }}>1. How many children are you registering interest for? <span style={{ color:C.muted, fontWeight:400, fontSize:13 }}>(max 3)</span></h3>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
          <button onClick={() => handleNumKids(numKids-1)} style={{ width:36, height:36, borderRadius:"50%", border:`2px solid ${C.purple}`, background:"#fff", color:C.purple, fontSize:20, fontWeight:700, cursor:"pointer" }}>−</button>
          <span style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:26, color:C.purple, minWidth:32, textAlign:"center" }}>{numKids}</span>
          <button onClick={() => handleNumKids(numKids+1)} style={{ width:36, height:36, borderRadius:"50%", border:`2px solid ${C.purple}`, background:"#fff", color:C.purple, fontSize:20, fontWeight:700, cursor:"pointer" }}>+</button>
        </div>
        <div style={{ display:"grid", gap:14 }}>
          {kids.map((kid, i) => (
            <div key={i} style={{ background:"#f9f6ff", borderRadius:12, padding:14 }}>
              <p style={{ fontWeight:700, color:C.purple, fontSize:14, marginBottom:10 }}>Child {i+1}</p>
              <div style={{ display:"grid", gap:10 }}>
                <div>
                  <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Name *</label>
                  <input className="input" placeholder="Child's full name" value={kid.name} onChange={e => updateKid(i,"name",e.target.value)}/>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Age *</label>
                  <input className="input" type="number" min="1" max="18" placeholder="Age" value={kid.age} onChange={e => updateKid(i,"age",e.target.value)} style={{ maxWidth:100 }}/>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Gender *</label>
                  <div style={{ display:"flex", gap:10 }}>
                    {["Girl","Boy"].map(g => (
                      <button key={g} type="button" onClick={() => updateKid(i,"gender",g)}
                        style={{ flex:1, padding:"10px", borderRadius:10, border:`2px solid ${kid.gender===g ? C.purple : C.border}`, background: kid.gender===g ? `${C.purple}15` : "#fff", fontWeight:700, fontSize:14, color: kid.gender===g ? C.purple : C.muted, cursor:"pointer", transition:"all 0.15s" }}>
                        {g==="Girl" ? "👧 Girl" : "👦 Boy"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Parent contact */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:16 }}>2. Parent / Caregiver Contact</h3>
        <div style={{ display:"grid", gap:12 }}>
          <div>
            <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Parent / Caregiver Name *</label>
            <input className="input" placeholder="e.g. Priya Patel" value={parent.name} onChange={e => setParent(p => ({...p, name:e.target.value}))}/>
          </div>
          <div>
            <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Email Address *</label>
            <input className="input" type="email" placeholder="e.g. priya@email.com" value={parent.email} onChange={e => setParent(p => ({...p, email:e.target.value}))}/>
          </div>
          <div>
            <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Phone Number *</label>
            <input className="input" type="tel" placeholder="e.g. (561) 555-1234" value={parent.phone} onChange={e => setParent(p => ({...p, phone:e.target.value}))}/>
          </div>
        </div>
      </div>

      {/* Acknowledgements */}
      <div className="card">
        <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:17, color:C.text, marginBottom:6 }}>3. Please Read & Acknowledge Each Section</h3>
        <p style={{ color:C.muted, fontSize:13, marginBottom:16 }}>Check the box and provide your initials for each section below.</p>

        <SectionBox id="showInfo" title="🎭 Show Info">
          <p><strong>Show date:</strong> Saturday, November 14th, in the evening. Location TBD.</p>
          <p style={{ marginTop:8 }}><strong>PBIA membership or ticket purchase</strong> is required for all non-performers. <a href="https://www.gopbia.org/membership/" target="_blank" rel="noreferrer" style={{ color:C.purple }}>Membership details →</a></p>
        </SectionBox>

        <SectionBox id="practices" title="📅 Practices">
          <p>Every <strong>Sunday from September 13th through November 7th</strong>, most likely <strong>10 a.m.–12 p.m.</strong></p>
          <p style={{ marginTop:8 }}>Each kid is allowed <strong>1 absence</strong>. Practice videos will be shared, and parents are responsible for catching their kids up on anything missed.</p>
          <p style={{ marginTop:8 }}><strong>Final practice:</strong> Friday, November 13th, and most likely a rehearsal at the venue on the 14th.</p>
        </SectionBox>

        <SectionBox id="fees" title="💰 Fees">
          <p>A <strong>required fee per kid (likely $30–40)</strong> to cover studio rental.</p>
          <p style={{ marginTop:8 }}>Matching <strong>costumes will be an additional cost</strong> (estimate TBD).</p>
        </SectionBox>

        <SectionBox id="expectations" title="🤝 Expectations">
          <p>Everyone teaching and organizing this is a volunteer, so we need parents as <strong>true partners</strong>: kids arriving on time, ready to focus, and practicing at home as needed.</p>
          <p style={{ marginTop:8 }}>A parent or guardian must <strong>remain on-site during practices</strong> and be willing to step in if their child needs a reset.</p>
          <p style={{ marginTop:8 }}>If a child is repeatedly disruptive or struggling to stay engaged, we'll chat with the parents, and in some cases, we may agree it's best to hold off on performing this year. This keeps practices fun and productive for the whole group.</p>
        </SectionBox>
      </div>

      {error && <p style={{ color:"#c0392b", fontWeight:700, marginBottom:12, textAlign:"center" }}>{error}</p>}

      <div style={{ textAlign:"center", paddingBottom:40 }}>
        {loading ? <div className="spinner"/> : (
          <button className="btn-primary" onClick={handleSubmit} style={{ fontSize:17, background:`linear-gradient(135deg, ${C.purple}, ${C.magenta})` }}>
            Submit Interest 🪔
          </button>
        )}
      </div>
    </div>
  );
}

// ── Admin Login ─────────────────────────────────────────────────────────
function AdminLogin({ adminPin, setAdminPin, enterAdmin, adminError }) {
  return (
    <div style={{ maxWidth:360, margin:"80px auto", padding:"0 20px" }}>
      <div className="card" style={{ textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🔐</div>
        <h2 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:22, color:C.text, marginBottom:16 }}>Admin Access</h2>
        <input className="input" type="password" placeholder="Enter PIN" value={adminPin}
          onChange={e => setAdminPin(e.target.value)}
          onKeyDown={e => e.key==="Enter" && enterAdmin()} style={{ textAlign:"center", marginBottom:14 }} />
        {adminError && <p style={{ color:"#c0392b", fontSize:13, marginBottom:10 }}>{adminError}</p>}
        <button className="btn-primary" onClick={enterAdmin}>Enter</button>
      </div>
    </div>
  );
}

// ── Admin Dashboard ─────────────────────────────────────────────────────
function AdminPage({ sessions, registrations }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [tab, setTab] = useState("registrations"); // registrations | classes | mailing | diwali
  const [mailingList, setMailingList] = useState([]);
  const [diwaliList, setDiwaliList] = useState([]);
  const [newSession, setNewSession] = useState({ date:"", time:"10:00 AM – 11:00 AM", location:"Ignite Sports Center", address:"860 Jupiter Park Dr B1, Jupiter, FL 33458", description:"" });
  const [editingId, setEditingId] = useState(null);
  const [editSession, setEditSession] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const allDates = Object.keys(registrations).sort();
  const upcoming = sessions.map(s => s.date);
  const allKeys = [...new Set([...upcoming, ...allDates])].sort();

  const viewRegs = selectedDate ? (registrations[selectedDate] || {}) : {};
  const viewList = Object.entries(viewRegs).map(([code, r]) => ({ code, ...r }));

  const removeReg = async (dk, code) => {
    if (!window.confirm("Remove this registration?")) return;
    await remove(ref(db, `registrations/${dk}/${code}`));
  };

  useEffect(() => {
    const mlRef = ref(db, "mailingList");
    onValue(mlRef, snap => {
      const data = snap.val() || {};
      setMailingList(Object.values(data).sort((a, b) => a.name.localeCompare(b.name)));
    });
    const dRef = ref(db, "diwaliInterest");
    onValue(dRef, snap => {
      const data = snap.val() || {};
      setDiwaliList(Object.entries(data).map(([id, v]) => ({id, ...v})).sort((a,b) => b.submittedAt.localeCompare(a.submittedAt)));
    });
  }, []);

  const addSession = async () => {
    if (!newSession.date) return;
    setSaving(true);
    const id = newSession.date.replace(/-/g, "");
    await set(ref(db, `sessions/${id}`), newSession);
    setNewSession({ date:"", time:"10:00 AM – 11:00 AM", location:"Ignite Sports Center", address:"860 Jupiter Park Dr B1, Jupiter, FL 33458" });
    setSaveMsg("Class added!"); setTimeout(() => setSaveMsg(""), 2500);
    setSaving(false);
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Remove this class date?")) return;
    await remove(ref(db, `sessions/${id}`));
  };

  const saveEdit = async () => {
    setSaving(true);
    await set(ref(db, `sessions/${editingId}`), editSession);
    setEditingId(null);
    setSaveMsg("Saved!"); setTimeout(() => setSaveMsg(""), 2500);
    setSaving(false);
  };

  const downloadCSV = () => {
    if (!selectedDate || viewList.length === 0) return;
    const rows = [["Date", "Parent Name", "Email", "Phone", "Child Name", "Child Age", "Registered At"]];
    viewList.forEach(r => {
      const kids = r.kids || [{ name: r.childName || "", age: "" }];
      kids.forEach(kid => {
        rows.push([r.sessionDate || selectedDate, r.parentName || "", r.email || "", r.phone || "", kid.name || "", kid.age || "", r.registeredAt ? new Date(r.registeredAt).toLocaleString() : ""]);
      });
    });
    const csv = rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `registrations-${selectedDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setTab(id)} style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:15, padding:"10px 24px", borderRadius:50, border:"none", cursor:"pointer", background: tab===id ? C.saffron : "#f0e8d8", color: tab===id ? "#fff" : C.text, transition:"all 0.15s" }}>{label}</button>
  );

  return (
    <div className="page" style={{ maxWidth:860, margin:"0 auto", padding:"32px 20px" }}>
      <h1 style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:28, color:C.text, marginBottom:6 }}>Admin Dashboard</h1>
      <p style={{ color:C.muted, marginBottom:20 }}>Manage classes and registrations.</p>

      {/* Tabs */}
      <div style={{ display:"flex", gap:10, marginBottom:28, flexWrap:"wrap" }}>
        <TabBtn id="registrations" label="📋 Registrations"/>
        <TabBtn id="classes" label="📅 Manage Classes"/>
        <TabBtn id="mailing" label="📬 Mailing List"/>
        <TabBtn id="diwali" label="🪔 Diwali Interest"/>
      </div>

      {/* ── REGISTRATIONS TAB ── */}
      {tab === "registrations" && <>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px,1fr))", gap:14, marginBottom:28 }}>
          {allKeys.map(key => {
            const count = registrations[key] ? Object.keys(registrations[key]).length : 0;
            const totalKids = registrations[key] ? Object.values(registrations[key]).reduce((sum, r) => sum + (r.kids ? r.kids.length : 1), 0) : 0;
            const d = new Date(key + "T12:00:00");
            return (
              <div key={key} onClick={() => setSelectedDate(key === selectedDate ? null : key)}
                style={{ background: selectedDate===key ? C.saffron : "#fff", borderRadius:16, padding:"16px 20px", cursor:"pointer", border:`2px solid ${selectedDate===key ? C.saffron : C.border}`, transition:"all 0.15s" }}>
                <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:26, color: selectedDate===key ? "#fff" : C.saffron }}>{totalKids}</div>
                <div style={{ fontWeight:700, fontSize:13, color: selectedDate===key ? "rgba(255,255,255,0.9)" : C.text }}>{formatDateShort(d)}</div>
                <div style={{ fontSize:11, color: selectedDate===key ? "rgba(255,255,255,0.75)" : C.muted }}>{count} {count===1?"family":"families"}</div>
              </div>
            );
          })}
        </div>

        {selectedDate && (() => {
          const allKids = viewList.flatMap(r => (r.kids || [{ name: r.childName||"—", age:"—" }]).map(k => ({ ...k, parentName: r.parentName, email: r.email, phone: r.phone, registeredAt: r.registeredAt, code: r.code })));
          const totalKids = allKids.length;

          return (
          <div className="card">
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:12 }}>
              <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:18, color:C.text, margin:0 }}>
                {formatDate(new Date(selectedDate + "T12:00:00"))}
              </h3>
              {viewList.length > 0 && (
                <button className="btn-secondary" onClick={downloadCSV} style={{ fontSize:13, padding:"8px 18px" }}>⬇ Download CSV</button>
              )}
            </div>

            {/* Stats */}
            {totalKids > 0 && (
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16 }}>
                <div style={{ background:C.saffron, color:"#fff", borderRadius:12, padding:"10px 18px", textAlign:"center", minWidth:80 }}>
                  <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24 }}>{totalKids}</div>
                  <div style={{ fontSize:11, opacity:0.9 }}>Total Kids</div>
                </div>
                <div style={{ background:C.teal, color:"#fff", borderRadius:12, padding:"10px 18px", textAlign:"center", minWidth:80 }}>
                  <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24 }}>{viewList.length}</div>
                  <div style={{ fontSize:11, opacity:0.9 }}>Families</div>
                </div>
              </div>
            )}

            {viewList.length === 0 && <p style={{ color:C.muted }}>No registrations yet.</p>}

            {/* Header row */}
            {allKids.length > 0 && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 2fr 90px auto", gap:8, padding:"8px 14px", background:C.saffron, borderRadius:10, marginBottom:8 }}>
                {["Child","Age","Parent · Contact","Registered",""].map((h,i) => (
                  <div key={i} style={{ fontWeight:700, color:"#fff", fontSize:12 }}>{h}</div>
                ))}
              </div>
            )}

            <div style={{ display:"grid", gap:6 }}>
              {viewList.flatMap(r => {
                const kids = r.kids || [{ name: r.childName||"—", age:"—" }];
                return kids.map((kid, kidIdx) => (
                  <div key={`${r.code}-${kidIdx}`} style={{ display:"grid", gridTemplateColumns:"1fr 60px 2fr 90px auto", gap:8, alignItems:"center", background: kidIdx===0 ? "#f9f6f0" : "#f2f0f8", borderRadius:10, padding:"10px 14px", borderLeft:`4px solid ${kidIdx===0 ? C.saffron : C.purple}` }}>
                    <div style={{ fontWeight:700, color:C.text, fontSize:14 }}>{kid.name}</div>
                    <div style={{ color:C.muted, fontSize:13 }}>Age {kid.age}</div>
                    <div>
                      <div style={{ fontWeight:600, color:C.text, fontSize:13 }}>{r.parentName}</div>
                      <div style={{ color:C.muted, fontSize:12 }}>{r.email}</div>
                      <div style={{ color:C.muted, fontSize:12 }}>{r.phone}</div>
                    </div>
                    <div style={{ color:C.muted, fontSize:11 }}>{new Date(r.registeredAt).toLocaleDateString()}</div>
                    {kidIdx===0 ? (
                      <button className="btn-danger" onClick={() => removeReg(selectedDate, r.code)} style={{ fontSize:11, padding:"6px 12px" }}>Remove</button>
                    ) : <div/>}
                  </div>
                ));
              })}
            </div>
          </div>
          );
        })()}
      </>}

      {/* ── CLASSES TAB ── */}
      {tab === "classes" && <>
        {saveMsg && <div style={{ background:`${C.teal}20`, border:`1px solid ${C.teal}`, borderRadius:12, padding:"10px 16px", marginBottom:16, color:C.teal, fontWeight:700 }}>✓ {saveMsg}</div>}

        {/* Add new class */}
        <div className="card" style={{ border:`2px solid ${C.gold}` }}>
          <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:18, color:C.text, marginBottom:16 }}>➕ Add a New Class</h3>
          <div style={{ display:"grid", gap:12 }}>
            <div>
              <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Date *</label>
              <input className="input" type="date" value={newSession.date} onChange={e => setNewSession(s => ({...s, date:e.target.value}))}/>
            </div>
            <div>
              <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Time</label>
              <input className="input" value={newSession.time} onChange={e => setNewSession(s => ({...s, time:e.target.value}))} placeholder="e.g. 10:00 AM – 11:00 AM"/>
            </div>
            <div>
              <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Location Name</label>
              <input className="input" value={newSession.location} onChange={e => setNewSession(s => ({...s, location:e.target.value}))} placeholder="e.g. Ignite Sports Center"/>
            </div>
            <div>
              <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Address</label>
              <input className="input" value={newSession.address} onChange={e => setNewSession(s => ({...s, address:e.target.value}))} placeholder="e.g. 860 Jupiter Park Dr B1, Jupiter, FL"/>
            </div>
            <div>
              <label style={{ display:"block", fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Special Description <span style={{ color:C.muted, fontWeight:400 }}>(optional — shown on the site)</span></label>
              <textarea className="input" value={newSession.description} onChange={e => setNewSession(s => ({...s, description:e.target.value}))} placeholder="e.g. This month we'll be learning Garba in preparation for Navratri!" rows={3} style={{ resize:"vertical" }}/>
            </div>
            <button className="btn-primary" onClick={addSession} disabled={saving || !newSession.date} style={{ alignSelf:"flex-start" }}>
              {saving ? "Saving..." : "Add Class"}
            </button>
          </div>
        </div>

        {/* Existing classes */}
        <div className="card">
          <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:18, color:C.text, marginBottom:16 }}>📅 Upcoming Classes</h3>
          {sessions.length === 0 && <p style={{ color:C.muted }}>No classes scheduled yet.</p>}
          <div style={{ display:"grid", gap:12 }}>
            {sessions.map(s => (
              <div key={s.id}>
                {editingId === s.id ? (
                  <div style={{ background:"#f9f6f0", borderRadius:14, padding:16, display:"grid", gap:10 }}>
                    <input className="input" type="date" value={editSession.date} onChange={e => setEditSession(x => ({...x, date:e.target.value}))}/>
                    <input className="input" value={editSession.time} onChange={e => setEditSession(x => ({...x, time:e.target.value}))} placeholder="Time"/>
                    <input className="input" value={editSession.location} onChange={e => setEditSession(x => ({...x, location:e.target.value}))} placeholder="Location name"/>
                    <input className="input" value={editSession.address} onChange={e => setEditSession(x => ({...x, address:e.target.value}))} placeholder="Address"/>
                    <textarea className="input" value={editSession.description || ""} onChange={e => setEditSession(x => ({...x, description:e.target.value}))} placeholder="Special description (optional)" rows={3} style={{ resize:"vertical" }}/>
                    <div style={{ display:"flex", gap:10 }}>
                      <button className="btn-primary" onClick={saveEdit} disabled={saving} style={{ padding:"10px 20px", fontSize:14 }}>{saving ? "Saving..." : "Save"}</button>
                      <button className="btn-secondary" onClick={() => setEditingId(null)} style={{ padding:"10px 20px", fontSize:14 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ background:"#f9f6f0", borderRadius:14, padding:"14px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                    <div>
                      <div style={{ fontFamily:"'Baloo 2'", fontWeight:700, color:C.text, fontSize:16 }}>{formatDate(new Date(s.date + "T12:00:00"))}</div>
                      <div style={{ color:C.muted, fontSize:13 }}>{s.time} · {s.location}</div>
                      {s.address && <div style={{ color:C.muted, fontSize:12 }}>{s.address}</div>}
                      {s.description && <div style={{ color:C.saffron, fontSize:13, fontStyle:"italic", marginTop:4 }}>"{s.description}"</div>}
                    </div>
                    <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                      <button className="btn-secondary" onClick={() => { setEditingId(s.id); setEditSession({...s}); }} style={{ fontSize:12, padding:"6px 14px" }}>Edit</button>
                      <button className="btn-danger" onClick={() => deleteSession(s.id)} style={{ fontSize:12, padding:"6px 14px" }}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </>}

      {/* ── MAILING LIST TAB ── */}
      {tab === "mailing" && <>
        <div className="card">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:12 }}>
            <div>
              <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:18, color:C.text, margin:0 }}>📬 Mailing List</h3>
              <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>{mailingList.length} subscriber{mailingList.length !== 1 ? "s" : ""} · auto-populated from registrations + manual signups</p>
            </div>
            {mailingList.length > 0 && (
              <button className="btn-secondary" onClick={() => {
                const csv = ["Name,Email,Added"].concat(mailingList.map(m => `"${m.name}","${m.email}","${m.addedAt ? new Date(m.addedAt).toLocaleDateString() : ""}"`)).join("\n");
                const blob = new Blob([csv], { type:"text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "mailing-list.csv"; a.click();
                URL.revokeObjectURL(url);
              }} style={{ fontSize:13, padding:"8px 18px" }}>⬇ Download CSV</button>
            )}
          </div>

          {mailingList.length === 0 && <p style={{ color:C.muted }}>No subscribers yet.</p>}

          {mailingList.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, padding:"8px 14px", background:C.saffron, borderRadius:10, marginBottom:8 }}>
              <div style={{ fontWeight:700, color:"#fff", fontSize:12 }}>Name</div>
              <div style={{ fontWeight:700, color:"#fff", fontSize:12 }}>Email</div>
              <div/>
            </div>
          )}

          <div style={{ display:"grid", gap:6 }}>
            {mailingList.map(m => (
              <div key={m.email} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, alignItems:"center", background:"#f9f6f0", borderRadius:10, padding:"10px 14px" }}>
                <div style={{ fontWeight:600, color:C.text, fontSize:14 }}>{m.name}</div>
                <div style={{ color:C.muted, fontSize:13 }}>{m.email}</div>
                <button className="btn-danger" onClick={async () => {
                  if (!window.confirm(`Remove ${m.email} from mailing list?`)) return;
                  const safeKey = m.email.toLowerCase().replace(/[.#$[\]]/g, "_");
                  await remove(ref(db, `mailingList/${safeKey}`));
                }} style={{ fontSize:11, padding:"6px 12px" }}>Remove</button>
              </div>
            ))}
          </div>
        </div>
      </>}

      {/* ── DIWALI INTEREST TAB ── */}
      {tab === "diwali" && <>
        <div className="card">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:12 }}>
            <div>
              <h3 style={{ fontFamily:"'Baloo 2'", fontWeight:700, fontSize:18, color:C.text, margin:0 }}>🪔 Diwali Performance Interest</h3>
              <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>
                {diwaliList.length} submission{diwaliList.length!==1?"s":""} · {diwaliList.reduce((sum,d) => sum + (d.kids ? d.kids.length : 0), 0)} kids total
              </p>
            </div>
            {diwaliList.length > 0 && (
              <button className="btn-secondary" onClick={() => {
                const rows = [["Submitted","Parent Name","Email","Phone","Child Name","Child Age","Gender","Show Info Initials","Practices Initials","Fees Initials","Expectations Initials"]];
                diwaliList.forEach(d => {
                  const kids = d.kids || [];
                  kids.forEach(kid => {
                    rows.push([
                      d.submittedAt ? new Date(d.submittedAt).toLocaleDateString() : "",
                      d.parent?.name || "",
                      d.parent?.email || "",
                      d.parent?.phone || "",
                      kid.name || "",
                      kid.age || "",
                      kid.gender || "",
                      d.initials?.showInfo || "",
                      d.initials?.practices || "",
                      d.initials?.fees || "",
                      d.initials?.expectations || "",
                    ]);
                  });
                });
                const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
                const blob = new Blob([csv], {type:"text/csv"});
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href=url; a.download="diwali-interest.csv"; a.click();
                URL.revokeObjectURL(url);
              }} style={{ fontSize:13, padding:"8px 18px" }}>⬇ Download CSV</button>
            )}
          </div>

          {diwaliList.length === 0 && <p style={{ color:C.muted }}>No submissions yet.</p>}

          {diwaliList.length > 0 && (() => {
            const allKids = diwaliList.flatMap(d => d.kids || []);
            const girls = allKids.filter(k => k.gender === "Girl");
            const boys = allKids.filter(k => k.gender === "Boy");
            const ageGroups = {};
            allKids.forEach(k => {
              const a = k.age || "?";
              if (!ageGroups[a]) ageGroups[a] = { girls:0, boys:0 };
              if (k.gender==="Girl") ageGroups[a].girls++;
              else if (k.gender==="Boy") ageGroups[a].boys++;
            });
            const sortedAges = Object.keys(ageGroups).sort((a,b) => Number(a)-Number(b));
            return (
              <div style={{ marginBottom:16 }}>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
                  <div style={{ background:C.purple, color:"#fff", borderRadius:12, padding:"10px 18px", textAlign:"center", minWidth:80 }}>
                    <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24 }}>{allKids.length}</div>
                    <div style={{ fontSize:11, opacity:0.9 }}>Total Kids</div>
                  </div>
                  <div style={{ background:"#E91E8C", color:"#fff", borderRadius:12, padding:"10px 18px", textAlign:"center", minWidth:80 }}>
                    <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24 }}>{girls.length}</div>
                    <div style={{ fontSize:11, opacity:0.9 }}>👧 Girls</div>
                  </div>
                  <div style={{ background:"#1565C0", color:"#fff", borderRadius:12, padding:"10px 18px", textAlign:"center", minWidth:80 }}>
                    <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24 }}>{boys.length}</div>
                    <div style={{ fontSize:11, opacity:0.9 }}>👦 Boys</div>
                  </div>
                  <div style={{ background:C.teal, color:"#fff", borderRadius:12, padding:"10px 18px", textAlign:"center", minWidth:80 }}>
                    <div style={{ fontFamily:"'Baloo 2'", fontWeight:800, fontSize:24 }}>{diwaliList.length}</div>
                    <div style={{ fontSize:11, opacity:0.9 }}>Families</div>
                  </div>
                </div>
                <div style={{ background:"#f9f6ff", borderRadius:12, padding:"12px 16px" }}>
                  <p style={{ fontWeight:700, fontSize:13, color:C.text, marginBottom:8 }}>By Age:</p>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {sortedAges.map(age => (
                      <div key={age} style={{ background:"#fff", borderRadius:10, padding:"6px 12px", border:`1px solid ${C.border}`, fontSize:12 }}>
                        <span style={{ fontWeight:700, color:C.text }}>Age {age}: </span>
                        {ageGroups[age].girls > 0 && <span style={{ color:"#E91E8C", fontWeight:700 }}>👧 {ageGroups[age].girls} </span>}
                        {ageGroups[age].boys > 0 && <span style={{ color:"#1565C0", fontWeight:700 }}>👦 {ageGroups[age].boys}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Header row */}
          {diwaliList.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 60px 2fr 90px auto", gap:8, padding:"8px 14px", background:C.purple, borderRadius:10, marginBottom:8 }}>
              {["Child","Age","Gender","Parent · Contact","Submitted",""].map((h,i) => (
                <div key={i} style={{ fontWeight:700, color:"#fff", fontSize:12 }}>{h}</div>
              ))}
            </div>
          )}

          <div style={{ display:"grid", gap:6 }}>
            {diwaliList.flatMap(d => {
              const kids = d.kids || [];
              return kids.map((kid, kidIdx) => (
                <div key={`${d.id}-${kidIdx}`} style={{ display:"grid", gridTemplateColumns:"1fr 60px 60px 2fr 90px auto", gap:8, alignItems:"center", background: kidIdx===0 ? "#f9f6ff" : "#f0ecff", borderRadius:10, padding:"10px 14px", borderLeft:`4px solid ${kidIdx===0 ? C.purple : C.magenta}` }}>
                  <div style={{ fontWeight:700, color:C.text, fontSize:14 }}>{kid.name}</div>
                  <div style={{ color:C.muted, fontSize:13 }}>Age {kid.age}</div>
                  <div style={{ fontSize:13, fontWeight:700, color: kid.gender==="Girl" ? "#E91E8C" : kid.gender==="Boy" ? "#1565C0" : C.muted }}>
                    {kid.gender==="Girl" ? "👧" : kid.gender==="Boy" ? "👦" : "—"}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, color:C.text, fontSize:13 }}>{d.parent?.name}</div>
                    <div style={{ color:C.muted, fontSize:12 }}>{d.parent?.email}</div>
                    <div style={{ color:C.muted, fontSize:12 }}>{d.parent?.phone}</div>
                    {kidIdx===0 && (
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:4 }}>
                        {["showInfo","practices","fees","expectations"].map(k => (
                          <span key={k} style={{ background: d.initials?.[k] ? `${C.purple}20` : "#ffeaea", color: d.initials?.[k] ? C.purple : "#c0392b", borderRadius:6, padding:"2px 6px", fontSize:10, fontWeight:700 }}>
                            {k==="showInfo"?"Show":k==="practices"?"Practices":k==="fees"?"Fees":"Exp"}: {d.initials?.[k] || "⚠️"}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ color:C.muted, fontSize:11 }}>{d.submittedAt ? new Date(d.submittedAt).toLocaleDateString() : ""}</div>
                  {kidIdx===0 ? (
                    <button className="btn-danger" onClick={async () => {
                      if (!window.confirm("Remove this Diwali interest submission?")) return;
                      await remove(ref(db, `diwaliInterest/${d.id}`));
                    }} style={{ fontSize:11, padding:"6px 12px" }}>Remove</button>
                  ) : <div/>}
                </div>
              ));
            })}
          </div>
        </div>
      </>}
    </div>
  );
}
